import React, { useEffect, useMemo } from "react";
import { useBooking } from "@/context/BookingContext";
import { StepTitle, PrimaryButton } from "@/components/BookingShell";

function Field({ label, value, onChange, type = "text", placeholder, testId, required, autoComplete, error }) {
    return (
        <label className="block" data-testid={`${testId}-field`}>
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/70 mb-2 font-semibold">{label}{required && <span className="text-gold ml-1">*</span>}</div>
            <input
                data-testid={testId}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoComplete={autoComplete}
                className="w-full bg-white/[0.04] border hairline rounded-xl focus:border-gold outline-none px-4 py-3 text-white placeholder-white/45 font-ui text-base transition-colors"
            />
            {error && <div className="mt-1 text-xs text-red-300">{error}</div>}
        </label>
    );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+()\-\s\d]{5,}$/;

export function DetailsStep({ setFooter }) {
    const { customer, setCustomer, next } = useBooking();

    const errors = useMemo(() => {
        const e = {};
        if (customer.firstName && customer.firstName.length < 2) e.firstName = "Enter your first name";
        if (customer.lastName && customer.lastName.length < 2) e.lastName = "Enter your last name";
        if (customer.email && !EMAIL_RE.test(customer.email)) e.email = "Enter a valid email address";
        if (customer.phone && !PHONE_RE.test(customer.phone)) e.phone = "Enter a valid phone number";
        return e;
    }, [customer]);

    const isValid =
        customer.firstName.trim().length >= 2 &&
        customer.lastName.trim().length >= 2 &&
        EMAIL_RE.test(customer.email) &&
        PHONE_RE.test(customer.phone) &&
        Object.keys(errors).length === 0;

    useEffect(() => {
        setFooter(
            <PrimaryButton disabled={!isValid} onClick={next} testId="details-continue-button">
                Review booking
            </PrimaryButton>
        );
        return () => setFooter(null);
    }, [isValid, next, setFooter]);

    const set = (k) => (v) => setCustomer({ ...customer, [k]: v });

    return (
        <div>
            <StepTitle
                eyebrow="Almost there"
                title={<>Your <em className="italic gold-text not-italic font-serif">details</em></>}
                subtitle="We use this to confirm your booking. We never share your information."
                testId="details-step-title"
            />

            <div className="space-y-6" data-testid="details-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="First name" value={customer.firstName} onChange={set("firstName")} placeholder="Isabella" testId="details-first-name" autoComplete="given-name" required error={errors.firstName} />
                    <Field label="Last name" value={customer.lastName} onChange={set("lastName")} placeholder="Ashcroft" testId="details-last-name" autoComplete="family-name" required error={errors.lastName} />
                </div>
                <Field label="Email" type="email" value={customer.email} onChange={set("email")} placeholder="isabella@example.com" testId="details-email" autoComplete="email" required error={errors.email} />
                <Field label="Phone" type="tel" value={customer.phone} onChange={set("phone")} placeholder="+44 7700 900123" testId="details-phone" autoComplete="tel" required error={errors.phone} />

                <label className="block" data-testid="details-notes-field">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-white/70 mb-2 font-semibold">Notes for Angela (optional)</div>
                    <textarea
                        data-testid="details-notes"
                        value={customer.notes}
                        onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
                        rows={3}
                        placeholder="Anything we should know?"
                        className="w-full bg-white/[0.04] border hairline focus:border-gold outline-none rounded-2xl p-4 text-white placeholder-white/45 font-ui text-sm transition-colors resize-none"
                    />
                </label>

                {/* Sprint 36.9 — marketing opt-in removed from the public
                    booking journey. Consent is now managed exclusively via
                    Angela's tooling + the one-click unsubscribe link on
                    each marketing send. */}
            </div>
        </div>
    );
}
